// src/components/ChatHistory.jsx
import React, { useRef, useEffect, useState } from 'react';
import './ChatHistory.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { defaultSchema } from 'hast-util-sanitize';
import removeMd from 'remove-markdown';

const ChatHistory = ({
  messages,
  setMessages,
  sendButtonVisible,
  suggestions,
  onSuggestionClick,
  isProcessing,
  translations: t,
  selectedVoice,
  searchHighlight,
  searchDirection,
}) => {
  const chatHistoryRef = useRef(null);
  const messageRefs = useRef({});
  const matchesRef = useRef([]);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [audioStates, setAudioStates] = useState({}); // Track loading states per message
  const [audioCache, setAudioCache] = useState({}); // Track audio URLs per message and voice
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [totalMatches, setTotalMatches] = useState(0);
  const [expandedImage, setExpandedImage] = useState(null);

  const WebSearchIcon = () => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8"/>
      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
      <line x1="11" y1="8" x2="11" y2="14"/>
      <line x1="8" y1="11" x2="14" y2="11"/>
    </svg>
  );

  const scrollToBottom = () => {
    if (chatHistoryRef.current) {
      chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isProcessing, suggestions]);

  const findAllMatches = (text, searchTerm) => {
    const matches = [];
    const lowerText = text.toLowerCase();
    const lowerTerm = searchTerm.toLowerCase();
    let pos = 0;
    
    while ((pos = lowerText.indexOf(lowerTerm, pos)) !== -1) {
      matches.push({
        start: pos,
        end: pos + lowerTerm.length,
        text: text.slice(pos, pos + lowerTerm.length)
      });
      pos += lowerTerm.length;
    }
    return matches;
  };

  useEffect(() => {
    if (searchHighlight) {
      const newMatches = [];
      
      // Find all matches in all messages, preserving order
      messages.forEach((msg, msgIndex) => {
        if (typeof msg.content === 'string') {
          const textMatches = findAllMatches(msg.content, searchHighlight);
          textMatches.forEach(match => {
            newMatches.push({
              messageIndex: msgIndex,
              ...match,
              globalPosition: newMatches.length
            });
          });
        } else if (Array.isArray(msg.content)) {
          msg.content.forEach(item => {
            if (item.type === 'text' && typeof item.text === 'string') {
              const textMatches = findAllMatches(item.text, searchHighlight);
              textMatches.forEach(match => {
                newMatches.push({
                  messageIndex: msgIndex,
                  ...match,
                  globalPosition: newMatches.length
                });
              });
            }
          });
        }
      });
      
      matchesRef.current = newMatches;
      setTotalMatches(newMatches.length);

      if (newMatches.length > 0) {
        let targetIndex;
        
        // Handle navigation
        if (searchDirection === 'next') {
          const currentPos = currentMatchIndex >= 0 ? 
            newMatches[currentMatchIndex].globalPosition : -1;
          targetIndex = newMatches.findIndex(m => m.globalPosition > currentPos);
          if (targetIndex === -1) targetIndex = 0;
        } else if (searchDirection === 'prev') {
          const currentPos = currentMatchIndex >= 0 ? 
            newMatches[currentMatchIndex].globalPosition : newMatches.length;
          targetIndex = newMatches.map(m => m.globalPosition)
            .reverse()
            .findIndex(pos => pos < currentPos);
          if (targetIndex === -1) targetIndex = newMatches.length - 1;
          else targetIndex = newMatches.length - 1 - targetIndex;
        } else {
          targetIndex = 0;
        }

        setCurrentMatchIndex(targetIndex);
        
        // Get the target match and scroll to it
        const targetMatch = newMatches[targetIndex];
        const messageEl = messageRefs.current[targetMatch.messageIndex];
        
        if (messageEl) {
          const highlightEls = messageEl.getElementsByClassName('highlight-text');
          const matchEl = highlightEls[targetMatch.globalPosition];
          
          if (matchEl) {
            matchEl.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'nearest'
            });
          }
        }
      }
    } else {
      matchesRef.current = [];
      setTotalMatches(0);
      setCurrentMatchIndex(0);
    }
  }, [searchHighlight, searchDirection, messages]);

  const highlightText = (text, searchTerm, messageIndex) => {
    if (!searchTerm || !text) return text;
    
    const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
    const currentMatch = matchesRef.current[currentMatchIndex];
    
    return parts.map((part, i) => {
      if (part.toLowerCase() === searchTerm.toLowerCase()) {
        // Add current-match class if this is the active match
        const isCurrentMatch = currentMatch && 
          currentMatch.messageIndex === messageIndex && 
          text.toLowerCase().indexOf(part.toLowerCase(), i > 0 ? parts.slice(0, i).join('').length : 0) === currentMatch.start;
        
        return (
          <span 
            key={i} 
            className={`highlight-text${isCurrentMatch ? ' current-match' : ''}`}
          >
            {part}
          </span>
        );
      }
      return part;
    });
  };

  const handlePlayTTS = async (messageIndex) => {
    const message = messages[messageIndex];
    const cacheKey = `${messageIndex}-${selectedVoice}`;

    // If audio is currently loading, do nothing
    if (audioStates[messageIndex] === 'loading') {
      return;
    }

    // If audio is currently playing
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setCurrentAudio(null);
      
      // If the same audio is being stopped, return early
      if (audioCache[cacheKey] && currentAudio.src === audioCache[cacheKey]) {
        return;
      }
    }

    // Check if audio URL is already cached for this voice
    if (audioCache[cacheKey]) {
      // Play the cached audio
      const audio = new Audio(audioCache[cacheKey]);
      setCurrentAudio(audio);

      audio.play().catch((error) => {
        console.error('Audio playback failed:', error);
      });

      audio.onended = () => {
        setCurrentAudio(null);
      };
      return;
    }

    // Show loading indicator
    setAudioStates((prev) => ({ ...prev, [messageIndex]: 'loading' }));

    try {
      // Get raw content first
      const rawContent = typeof message.content === 'string'
        ? message.content
        : Array.isArray(message.content)
          ? message.content.map((item) => (item.text || '')).join('\n')
          : '';

      // Clean the text while preserving lists
      const cleanText = rawContent
        .replace(/!\[.*?\]\(.*?\)/g, '')
        .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1')
        .replace(/[*_]{1,2}([^*_]+)[*_]{1,2}/g, '$1')
        .replace(/`[^`]+`/g, '$1')
        .replace(/```[\s\S]+?```/g, '')
        .replace(/^[-*]\s+/gm, '• ')
        .replace(/^\d+\.\s+/gm, (match) => match)
        .replace(/\n{3,}/g, '\n\n')
        .replace(/[ \t]+/g, ' ')
        .trim();

      const response = await fetch('/api/tts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: cleanText,
          voice: selectedVoice,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch TTS audio');
      }

      const audioBlob = await response.blob();
      const audioURL = URL.createObjectURL(audioBlob);

      // Cache the audio URL for this voice
      setAudioCache(prev => ({
        ...prev,
        [cacheKey]: audioURL
      }));

      // Update audioStates
      setAudioStates((prev) => ({ ...prev, [messageIndex]: 'loaded' }));

      // Play the audio
      const audio = new Audio(audioURL);
      setCurrentAudio(audio);

      audio.play().catch((error) => {
        console.error('Audio playback failed:', error);
      });

      audio.onended = () => {
        setCurrentAudio(null);
      };
    } catch (error) {
      console.error('Error playing TTS:', error);
      setAudioStates((prev) => ({ ...prev, [messageIndex]: 'error' }));
    }
  };

  const schema = {
    ...defaultSchema,
    attributes: {
      ...defaultSchema.attributes,
      div: [
        ...(defaultSchema.attributes.div || []),
        'className',
        'class',
      ],
      img: [
        ...(defaultSchema.attributes.img || []),
        'className',
        'class',
      ],
    },
  };

  const renderMessageContent = (content, messageIndex) => {
    if (typeof content === 'string') {
      // First highlight any matches
      const highlightedContent = searchHighlight ? highlightText(content, searchHighlight, messageIndex) : content;
      
      // If the content was highlighted, it's now React elements and should be rendered directly
      if (searchHighlight) {
        return highlightedContent;
      }
      
      // Otherwise, render as markdown with plugins
      return (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[
            rehypeRaw,
            [rehypeSanitize, schema],
          ]}
        >
          {content}
        </ReactMarkdown>
      );
    } else if (Array.isArray(content)) {
      return content.map((item, index) => {
        if (item.type === 'text') {
          // Same logic for array items
          const highlightedText = searchHighlight ? highlightText(item.text, searchHighlight, messageIndex) : item.text;
          if (searchHighlight) {
            return <div key={index}>{highlightedText}</div>;
          }
          return (
            <ReactMarkdown
              key={index}
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[
                rehypeRaw,
                [rehypeSanitize, schema],
              ]}
            >
              {item.text}
            </ReactMarkdown>
          );
        } else if (item.type === 'image' && item.source?.url) {
          return <img key={index} src={item.source.url} alt="Uploaded" className="message-image" />;
        } else if (item.type === 'document' && item.source?.url) {
          return (
            <div key={index} className="pdf-placeholder">
              📄 PDF Document Uploaded
            </div>
          );
        }
        return null;
      });
    }
    return null;
  };

  const handleSuggestionClick = (suggestion) => {
    if (suggestion === 'Regenerate Response') {
      // Find the last user message and re-send it
      const lastUserMessageIndex = messages.length - 2;
      if (lastUserMessageIndex >= 0 && messages[lastUserMessageIndex].role === 'user') {
        onSuggestionClick(messages[lastUserMessageIndex].content, true); // Pass true to indicate regeneration
      }
    } else {
      onSuggestionClick(suggestion);
    }
  };

  const handleImageClick = (e) => {
    if (e.target.tagName === 'IMG') {
      if (expandedImage) {
        setExpandedImage(null);
        e.target.classList.remove('expanded');
        document.body.style.overflow = 'auto';
      } else {
        setExpandedImage(e.target.src);
        e.target.classList.add('expanded');
        document.body.style.overflow = 'hidden';
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (expandedImage && !e.target.closest('.image-grid')) {
        setExpandedImage(null);
        document.querySelectorAll('.image-grid img.expanded')
          .forEach(img => img.classList.remove('expanded'));
        document.body.style.overflow = 'auto';
      }
    };

    if (expandedImage) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [expandedImage]);

  return (
    <div className="chat-history" ref={chatHistoryRef}>
      {messages.map((message, index) => (
        <div
          key={index}
          className={`message ${message.role} ${message.hidden ? 'hidden' : ''} ${
            message.webSearchEnabled ? 'with-web-search' : ''
          }`}
          ref={el => messageRefs.current[index] = el}
          onClick={handleImageClick}
        >
          {message.role === 'user' && message.webSearchEnabled && (
            <div className="web-search-indicator">
              <WebSearchIcon />
              <span>{t('webSearchEnabled')}</span>
            </div>
          )}
          {message.role === 'assistant' && (
            <button
              className="message-tts-button"
              onClick={() => handlePlayTTS(index)}
              title="Play audio"
            >
              {/* Speaker icon or loading indicator */}
              {audioStates[index] === 'loading' ? (
                <div className="loading-spinner"></div>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M11 5L6 9H2v6h4l5 4V5z" />
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                </svg>
              )}
            </button>
          )}
          <div className="message-content">
            {renderMessageContent(message.content, index)}
          </div>
        </div>
      ))}

      {isProcessing && (
        <div className="thinking-indicator">
          {t('thinking').replace('...', '')}
          <div className="dots">
            <span>.</span>
            <span>.</span>
            <span>.</span>
          </div>
        </div>
      )}

      {suggestions.length > 0 && !isProcessing && (
        <div className="suggestions-container">
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              className={`suggestion-button ${suggestion === 'Regenerate Response' ? 'regenerate' : ''}`}
              onClick={() => handleSuggestionClick(suggestion)}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChatHistory;
